import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link as MuiLink,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SeoHead from '../components/SeoHead';
import { CitationLine, type CitationSource, type MentionedGame } from '../components/CitationLine';
import { brandingFixture } from '../lib/branding/branding-fixture';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  approvePendingThunk,
  fetchPendingList,
  rejectPendingThunk,
} from '../store/thunks/pending-thunks';
import type { PendingContentItem } from '../lib/api/pending-api-client';
import { decodeEnvelope, type ContentEnvelope } from '../lib/content/envelope-builder';

/**
 * Pending-queue review page.
 *
 * Every card decodes the pending record's body envelope and shows
 * the structured fields (title, hero image, platforms, gallery,
 * purchase links, citation) — the same fields the consumer-facing
 * card renders after approval. Approving is one click; rejecting
 * opens a required-reason dialog (1-500 chars, backend enforces).
 */
export default function AdminPendingPage(): JSX.Element {
  const dispatch = useAppDispatch();
  const items = useAppSelector((state) => state.pending.items);
  const listStatus = useAppSelector((state) => state.pending.listStatus);
  const listError = useAppSelector((state) => state.pending.listError);
  const actionError = useAppSelector((state) => state.pending.actionError);
  const approveStatus = useAppSelector((state) => state.pending.approveStatus);
  const rejectStatus = useAppSelector((state) => state.pending.rejectStatus);
  const [rejectTarget, setRejectTarget] = useState<PendingContentItem | undefined>(undefined);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    void dispatch(fetchPendingList(1));
  }, [dispatch]);

  const busy = approveStatus === 'loading' || rejectStatus === 'loading';

  return (
    <Stack spacing={3} sx={{ maxWidth: 900, mx: 'auto', mt: 4 }}>
      <SeoHead
        route="/admin/pending"
        pageTitle="Pending queue"
        branding={brandingFixture.share}
        jsonLdType="WebSite"
      />
      <Typography variant="h1" component="h1" sx={{ fontSize: '1.75rem' }}>
        Pending queue
      </Typography>
      {listStatus === 'loading' && items.length === 0 && (
        <Stack direction="row" spacing={2} sx={{ alignItems: 'center', p: 4 }}>
          <CircularProgress size={24} />
          <Typography>Loading queue…</Typography>
        </Stack>
      )}
      {listStatus === 'failed' && (
        <Alert severity="error">Failed to load queue: {listError}</Alert>
      )}
      {actionError && <Alert severity="error">{actionError}</Alert>}
      {listStatus === 'succeeded' && items.length === 0 && (
        <Alert severity="info">The pending queue is empty.</Alert>
      )}
      <Stack spacing={2}>
        {sortForReview(items).map((item) => (
          <PendingCard
            key={item.id}
            item={item}
            onApprove={() => void dispatch(approvePendingThunk(item.id))}
            onReject={() => {
              setRejectTarget(item);
              setRejectReason('');
            }}
            disabled={busy}
          />
        ))}
      </Stack>
      <RejectDialog
        target={rejectTarget}
        reason={rejectReason}
        onReasonChange={setRejectReason}
        onCancel={() => setRejectTarget(undefined)}
        onConfirm={() => {
          if (rejectTarget === undefined) return;
          void dispatch(rejectPendingThunk(rejectTarget.id, rejectReason));
          setRejectTarget(undefined);
        }}
      />
    </Stack>
  );
}

/**
 * Sort pending items for the review surface: by target publish date
 * ascending (soonest first), then by rank within the same date so
 * the top-3 auto-publish items sit above the candidate items. Items
 * with no `autoApproveAt` (candidates without a TTL) sort to the end.
 */
function sortForReview(
  items: ReadonlyArray<PendingContentItem>,
): ReadonlyArray<PendingContentItem> {
  const withKey = items.map((item) => {
    const envelope = decodeEnvelope(item.body);
    const rawRank = (envelope as { rank?: unknown }).rank;
    const rank = typeof rawRank === 'number' && rawRank > 0 ? rawRank : Number.MAX_SAFE_INTEGER;
    // Prefer intendedPublishDate (present on every current ingest
    // boundary) over autoApproveAt (absent on operator-loaded facts).
    // Landed 2026-09-12 so admin-loaded facts sort next to the
    // composer-ingested news/release rows targeting the same day.
    const targetMs = deriveTargetMs(item);
    return { item, targetMs, rank };
  });
  withKey.sort((a, b) => a.targetMs - b.targetMs || a.rank - b.rank);
  return withKey.map((x) => x.item);
}

function deriveTargetMs(item: PendingContentItem): number {
  if (typeof item.intendedPublishDate === 'string'
    && /^\d{4}-\d{2}-\d{2}$/.test(item.intendedPublishDate)) {
    const ms = Date.parse(`${item.intendedPublishDate}T12:00:00.000Z`);
    if (Number.isFinite(ms)) return ms;
  }
  if (item.autoApproveAt.length > 0) {
    const ms = Date.parse(item.autoApproveAt);
    if (Number.isFinite(ms)) return ms;
  }
  return Number.MAX_SAFE_INTEGER;
}

interface PendingCardProps {
  readonly item: PendingContentItem;
  readonly onApprove: () => void;
  readonly onReject: () => void;
  readonly disabled: boolean;
}

function PendingCard(props: PendingCardProps): JSX.Element {
  const { item, onApprove, onReject, disabled } = props;
  const envelope = decodeEnvelope(item.body);
  const rawRank = (envelope as { rank?: unknown }).rank;
  const rank = typeof rawRank === 'number' && rawRank > 0 ? rawRank : undefined;
  return (
    <Card>
      <CardContent>
        <PendingMeta item={item} rank={rank} />
        <Box sx={{ mt: 2, borderTop: '1px dashed', borderColor: 'divider', pt: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Preview — rendered as it will appear on the Daily page
          </Typography>
          <WysiwygPreview item={item} envelope={envelope} />
        </Box>
        <EnvelopeExtras envelope={envelope} />
      </CardContent>
      <CardActions>
        <Button variant="contained" color="success" onClick={onApprove} disabled={disabled}>
          Approve
        </Button>
        <Button variant="outlined" color="error" onClick={onReject} disabled={disabled}>
          Reject
        </Button>
      </CardActions>
    </Card>
  );
}

/**
 * WYSIWYG preview — renders a pending item using the same visual
 * structure as the DailyPage's NewsItem: floated image, title,
 * wrapped body, and the round-26 CitationLine (deduplicated, varied
 * phrasing, game-page hyperlinks) — so what the admin approves is
 * exactly what visitors will see.
 */
function WysiwygPreview({
  item, envelope,
}: {
  readonly item: PendingContentItem;
  readonly envelope: ContentEnvelope;
}): JSX.Element {
  const isNews = item.type === 'news';
  const isFact = item.type === 'fact';
  const title = envelope.title ?? '';
  const body = envelope.body ?? '';
  const image = envelope.image;
  const sources = buildSourcesFromEnvelope(envelope);
  const games = buildMentionedGames(envelope);
  const imageFloat: 'left' | 'right' = 'left';
  return (
    <Box sx={{ overflow: 'hidden' }}>
      {(isNews || isFact) && title.length > 0 && (
        <Typography
          variant="h4"
          component="h3"
          sx={{ fontSize: { xs: '1rem', sm: '1.125rem' }, fontWeight: 600, mb: 1.5 }}
        >
          {title}
        </Typography>
      )}
      {image !== undefined && (
        <Box
          component="img"
          src={image}
          alt={title || 'preview image'}
          sx={{
            float: { xs: 'none', sm: imageFloat },
            width: { xs: '100%', sm: 'auto' },
            maxWidth: { xs: '100%', sm: '50%' },
            maxHeight: { sm: 240 },
            objectFit: 'contain',
            borderRadius: 1,
            mr: { sm: 2 }, mb: { xs: 1.5, sm: 1 },
          }}
        />
      )}
      {body.length > 0 && <Typography variant="body1">{body}</Typography>}
      <CitationLine hashKey={item.id} sources={sources} mentionedGames={games} />
    </Box>
  );
}

/**
 * Merge the envelope's primary source + corroborating sources into
 * the CitationLine-expected shape, deduplicating and preserving
 * order (primary first, then corroborating).
 */
function buildSourcesFromEnvelope(envelope: ContentEnvelope): ReadonlyArray<CitationSource> {
  const out: CitationSource[] = [];
  if (typeof envelope.source === 'string' && envelope.source.length > 0) {
    out.push({ name: envelope.source, url: envelope.sourceUrl ?? '' });
  }
  const corroborating = (envelope as { corroboratingSources?: unknown }).corroboratingSources;
  if (Array.isArray(corroborating)) {
    for (const entry of corroborating) {
      if (entry === null || typeof entry !== 'object') continue;
      const s = (entry as Record<string, unknown>)['source'];
      const u = (entry as Record<string, unknown>)['url'];
      if (typeof s === 'string' && s.length > 0 && typeof u === 'string' && u.length > 0) {
        out.push({ name: s, url: u });
      }
    }
  }
  return out;
}

function buildMentionedGames(envelope: ContentEnvelope): ReadonlyArray<MentionedGame> {
  const raw = (envelope as { mentionedGames?: unknown }).mentionedGames;
  if (!Array.isArray(raw)) return [];
  const out: MentionedGame[] = [];
  for (const entry of raw) {
    if (entry === null || typeof entry !== 'object') continue;
    const g = (entry as Record<string, unknown>)['gameId'];
    const t = (entry as Record<string, unknown>)['title'];
    if (typeof g === 'string' && g.length > 0 && typeof t === 'string' && t.length > 0) {
      out.push({ gameId: g, title: t });
    }
  }
  return out;
}

function PendingMeta({
  item,
  rank,
}: {
  readonly item: PendingContentItem;
  readonly rank: number | undefined;
}): JSX.Element {
  const ingestedDate = new Date(item.ingestedAt);
  const autoApproveDate =
    item.autoApproveAt.length > 0 ? new Date(item.autoApproveAt) : undefined;
  // Prefer intendedPublishDate for the "publishes on X" chip — every
  // current ingest boundary stamps this. Fall back to autoApproveAt
  // for legacy rows.
  const publishesOnLabel = pickPublishesOnLabel(item, autoApproveDate);
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
      <Chip label={item.type} size="small" color="primary" />
      {rank !== undefined && (
        <Chip
          label={`rank #${rank}`}
          size="small"
          color={rank <= 3 ? 'success' : 'warning'}
          variant={rank <= 3 ? 'filled' : 'outlined'}
        />
      )}
      {publishesOnLabel !== undefined ? (
        <Chip
          label={`publishes ${publishesOnLabel}`}
          size="small"
          color="info"
          variant="outlined"
        />
      ) : (
        <Chip label="candidate (human approval only)" size="small" variant="outlined" />
      )}
      <Chip label={`from ${item.source}`} size="small" variant="outlined" />
      <Chip label={item.id} size="small" variant="outlined" sx={{ maxWidth: 200 }} />
      <Box sx={{ flexGrow: 1 }} />
      <Typography variant="caption" color="text.secondary">
        v{item.version} · ingested {ingestedDate.toLocaleString()}
        {autoApproveDate !== undefined
          ? ` · auto-approves ${autoApproveDate.toLocaleString()}`
          : ' · no auto-approve TTL'}
      </Typography>
    </Stack>
  );
}

function pickPublishesOnLabel(
  item: PendingContentItem,
  autoApproveDate: Date | undefined,
): string | undefined {
  if (typeof item.intendedPublishDate === 'string'
    && /^\d{4}-\d{2}-\d{2}$/.test(item.intendedPublishDate)) {
    const anchored = new Date(`${item.intendedPublishDate}T12:00:00.000Z`);
    if (Number.isFinite(anchored.getTime())) {
      return anchored.toLocaleDateString(undefined, {
        weekday: 'short', month: 'short', day: 'numeric',
      });
    }
  }
  if (autoApproveDate !== undefined) {
    return autoApproveDate.toLocaleDateString(undefined, {
      weekday: 'short', month: 'short', day: 'numeric',
    });
  }
  return undefined;
}

function EnvelopeExtras({ envelope }: { readonly envelope: ContentEnvelope }): JSX.Element | null {
  const hasPlatforms = envelope.platforms !== undefined && envelope.platforms.length > 0;
  const hasGallery = envelope.gallery !== undefined && envelope.gallery.length > 0;
  const hasVideos = envelope.videos !== undefined && envelope.videos.length > 0;
  const hasPurchase = envelope.purchaseLinks !== undefined && envelope.purchaseLinks.length > 0;
  const hasSource = envelope.source !== undefined || envelope.sourceUrl !== undefined;
  if (!hasPlatforms && !hasGallery && !hasVideos && !hasPurchase && !hasSource && envelope.gameId === undefined) {
    return null;
  }
  return (
    <Stack spacing={1} sx={{ mt: 2 }}>
      {hasPlatforms && (
        <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap' }}>
          {envelope.platforms!.map((p) => (
            <Chip key={p} label={p} size="small" />
          ))}
        </Stack>
      )}
      {envelope.gameId !== undefined && (
        <Typography variant="caption" color="text.secondary">
          Game id: {envelope.gameId}
        </Typography>
      )}
      {hasPurchase && (
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Purchase links
          </Typography>
          <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap' }}>
            {envelope.purchaseLinks!.map((p, i) => (
              <MuiLink
                key={`${p.vendor}-${i}`}
                href={p.url}
                target="_blank"
                rel="noopener noreferrer"
                variant="body2"
              >
                {p.vendor}
              </MuiLink>
            ))}
          </Stack>
        </Box>
      )}
      {hasGallery && (
        <Typography variant="caption" color="text.secondary">
          Gallery: {envelope.gallery!.length} image{envelope.gallery!.length === 1 ? '' : 's'}
        </Typography>
      )}
      {hasVideos && (
        <Typography variant="caption" color="text.secondary">
          Videos: {envelope.videos!.length}
        </Typography>
      )}
      {hasSource && (
        <Typography variant="caption" color="text.secondary">
          Source: {envelope.source}
          {envelope.sourceUrl !== undefined && (
            <>
              {' '}
              (
              <MuiLink href={envelope.sourceUrl} target="_blank" rel="noopener noreferrer">
                {envelope.sourceUrl}
              </MuiLink>
              )
            </>
          )}
        </Typography>
      )}
    </Stack>
  );
}

interface RejectDialogProps {
  readonly target: PendingContentItem | undefined;
  readonly reason: string;
  readonly onReasonChange: (v: string) => void;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
}

function RejectDialog(props: RejectDialogProps): JSX.Element {
  const open = props.target !== undefined;
  const tooShort = props.reason.trim().length === 0;
  const tooLong = props.reason.length > 500;
  return (
    <Dialog open={open} onClose={props.onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Reject pending item</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Explain why this item is being rejected. Length 1-500 characters. This reason is
          persisted on the record for audit.
        </Typography>
        <TextField
          label="Reason"
          value={props.reason}
          onChange={(e) => props.onReasonChange(e.target.value)}
          fullWidth
          multiline
          minRows={3}
          error={tooLong}
          helperText={
            tooLong ? `${props.reason.length} / 500 — too long` : `${props.reason.length} / 500`
          }
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onCancel}>Cancel</Button>
        <Button
          variant="contained"
          color="error"
          onClick={props.onConfirm}
          disabled={tooShort || tooLong}
        >
          Reject
        </Button>
      </DialogActions>
    </Dialog>
  );
}
